import { cn } from '@workspace/ui/lib/utils';
import { useLocation } from 'react-router-dom';

import { company } from '~/company/Company';
import {
  ProductCarouselDesktop,
  ProductCarouselMobile,
} from '~/components/isolated/Carousel';
import ProductCardSkeletonFactory from '~/components/ProductCardV3/skeletons/ProductCardSkeletonFactory';
import ProductCardS from '~/components/ProductCardV3/variants/ProductCardS/ProductCardS';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import { createCategoryUrl } from './shared/utils';

interface ProductsCarouselSliderSuperPricesProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
}

const containerClass = 'mx-auto w-full max-w-(--layout-max-width)';
const slideClass =
  'basis-[88px]! shrink-0 grow-0 pl-0! [&:not(:last-child)]:mr-4 [&>div]:w-full md:basis-[calc(1/6*100%-16px+calc(16px/6))]!';
const slideMarketplaceClass =
  'w-[88px] shrink-0 pl-0! [&:not(:last-child)]:mr-4 [&>div]:w-[88px] md:w-[338px] md:[&>div]:w-[338px]';
const itemClass = 'w-[88px] shrink-0 md:w-[227px]';
const carouselContentClass = 'pb-2 -mx-4 px-4 w-[calc(100%+32px)]!';
const skeletonContainerClass =
  'flex gap-4 [&>*]:w-[88px] [&>*]:shrink-0 md:[&>*]:w-[227px]';

export const ProductsCarouselSliderSuperPrices: React.FC<
  ProductsCarouselSliderSuperPricesProps
> = ({ category, className, ...props }) => {
  const { pathname } = useLocation();
  const { isMobile } = useGlobal();

  if (!category.products.length && category.productsRequest.isFulfilled) {
    return null;
  }

  const categoryId = category?.subcategory[0]?.id;

  if (!category.subcategory.length || !categoryId) {
    return null;
  }

  const linkTo =
    pathname !== '/search' ? createCategoryUrl({ id: categoryId }) : undefined;

  const products = category.products.slice(0, isMobile ? 10 : 15);
  const items = products.map((product) => ({
    id: product.id,
    node: <ProductCardS product={product} className="w-full!" />,
  }));

  const skeletonComponent = (
    <div className={skeletonContainerClass}>
      {Array.from({ length: 5 }, (_, i) => (
        <ProductCardSkeletonFactory key={`skeleton-${i}`} variant="s" />
      ))}
    </div>
  );

  const commonProps = {
    title: category.name,
    linkTo,
    categoryId,
    items,
    skeletonComponent: items.length === 0 ? skeletonComponent : undefined,
    headerImage: category.previewImage,
  };

  return (
    <div {...props} className={cn(containerClass, className)}>
      {isMobile ? (
        <ProductCarouselMobile
          {...commonProps}
          showViewAllOnMobile
          itemClassName={itemClass}
        />
      ) : (
        <ProductCarouselDesktop
          {...commonProps}
          navigationVariant="header"
          slideClassName={
            company.isMarketplaceVariant ? slideMarketplaceClass : slideClass
          }
          contentClassName={carouselContentClass}
        />
      )}
    </div>
  );
};
